/**
 * @fileoverview Gate de conta obrigatória (I5+I6) — a porta ÚNICA de entrada
 * do app TV. Conta Swipe é obrigatória pra tudo: sem sessão Swipe, o usuário
 * cai aqui (nunca no antigo caminho anônimo `/onboarding/mode`).
 *
 * Desenho: docs/specs/onboarding-entrada-tv-ux.md §3. É o "experimentar grátis
 * em 1 gesto" — QR em destaque (grande, tier-aware), copy que vende o trial,
 * instruções 1-2-3 e o código em destaque (caminho universal sem câmera). NÃO é
 * uma parede de login fria.
 *
 * Reusa o motor existente: `usePairingFlow` (init → polling → finalize, persiste
 * a sessão Swipe) + `PairingShell` (stages) + `QRDisplay`. Esta tela só decide a
 * VARIANTE de copy e o comportamento do BACK/Cancelar.
 *
 * Três variantes (mesma tela — a copy é escolhida por `resolveGateVariant`):
 * - `entry`: 1ª entrada / 2º+ boot sem nenhuma sessão. Copy "comece grátis".
 * - `link`: BASE INSTALADA — já está logado só com Xtream (tem `isAuthed`), mas
 *   sem conta Swipe e sem ter vinculado antes. Copy ACOLHEDORA "vincule sua conta
 *   pra levar seus dados". NÃO desloga nem apaga nada: ao parear, o sync migra os
 *   dados locais pra conta. (I6 — gate de vínculo.)
 * - `reconnect`: o aparelho JÁ vinculou uma conta Swipe (marcador durável
 *   `iptv:swipe-linked`) e a sessão CAIU (401 unrecoverable). Copy "sua sessão
 *   caiu, reconecte" — nada foi perdido, é só re-parear. Distingue "usuário que
 *   já era" de "aparelho novo", que sem o marcador cairiam os dois no gate frio.
 *
 * BACK / Cancelar: o gate é a raiz efetiva (não há "trás" sem dead-end). Abre
 * confirmação de SAIR do app — nunca fecha sem confirmar. (spec §4.)
 *
 * FIX 2026-07 (achados da LG real — sem rota de escape):
 *  - O botão de escape dizia "Voltar" mas SAI do app. Copy corrigida pra "Sair"
 *    (`cancelLabel`), mantendo o comportamento.
 *  - A saída era `window.close()` — no-op em Tizen e em parte das TVs. Agora usa
 *    `exitApp()` (capability) e, se a plataforma NÃO fechar, mostra o caminho
 *    real ("use o botão Home"), em vez de deixar o usuário achando que travou.
 *  - Chamávamos `cancel()` (que navega pra `/`) ANTES de fechar: quando o close
 *    falhava, o RootRoute remontava o gate e reiniciava o pairing = loop sem fim.
 *    O `cancel()` saiu do caminho de saída.
 *  - Variante `link` (base instalada, logada só no Xtream): ganhou "Sair da conta"
 *    — a rota de escape de quem está preso num estado autenticado inconsistente.
 */

import { useTranslation } from '@app-iptv/i18n'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { getSiteUrl } from '../api'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { PairingShell } from '../components/PairingShell'
import { useAppExit } from '../hooks/useAppExit'
import { useBackKey } from '../hooks/useBackKey'
import { usePairingFlow } from '../hooks/usePairingFlow'
import { readQrSize } from '../lib/card-metrics'
import { signOut } from '../sign-out'
import { useSessionStore } from '../store'
import { wasSwipeLinked } from '../swipe-linked-flag'
import { hasSwipeSession } from '../sync'

import { resolveGateVariant } from './gate-variant'

/** Tamanho do QR no gate. Sai do TOKEN `--qr-size` (index.css), que varia por
 *  tier (`html[data-tier=...]`: um QR maior custa raster em device fraco) e por
 *  canvas (`html[data-tv-scale]`). Na TV @1920: vlow/low 360, mid 440,
 *  high/ultra 480 — o spec (docs/specs/onboarding-entrada-tv-ux.md) pede >=420
 *  em mid/high com piso 360, porque distância confiável de leitura é ~10x o lado
 *  do código e a câmera do celular mira de 2,5-3m.
 *
 *  ===== NÃO ENCOLHA ISTO "PRA CABER" =====
 *  Este bloco já disse que 440px "empurrava o Cancelar pra fora da viewport", e
 *  foi por isso que o QR virou 220 e o dono reclamou de QR pequeno. Aquilo valia
 *  quando `.gateContainer` era `justify-content: flex-start`; hoje é `center`
 *  (AccountLoginScreen.module.css). MEDIDO no gate real, viewport 1920x1080,
 *  boot de TV: mid 440 -> gateMain 478px, ações em 851-919; high 480 ->
 *  gateMain 514px, ações em 869-937, 143px de folga até o rodapé.
 *  `scrollHeight === clientHeight === 1080` nos dois: ZERO scroll.
 *
 *  Era um switch de tier em JS × getUiScale() — o último consumidor do
 *  multiplicador --ui-scale, que morreu quando a base de TV foi rebaseada. Agora
 *  o número mora no CSS (fonte única) e o JS só o lê. */
function qrSizeForTier(): number {
  return Math.round(readQrSize())
}

export function AccountGateScreen() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { stage, retry, cancel, configureHere } = usePairingFlow()
  const [confirmExit, setConfirmExit] = useState(false)
  const [confirmSignOut, setConfirmSignOut] = useState(false)
  const { requestExit, exitFailed, dismissExitFailed } = useAppExit()

  // URL do site vem do runtime-config (env SITE_URL da API), com fallback
  // síncrono (VITE_SITE_URL → literal). Computada no RENDER, nunca em const de
  // módulo: o runtime-config carrega no boot e uma const top-level congelaria o
  // fallback antes do wire. getSiteUrl() nunca retorna undefined, então o QR
  // (CTA crítico de pareamento) tem URL válida já no 1º paint.
  const siteUrl = getSiteUrl()
  const qrHref = `${siteUrl}/parear`
  const qrBaseLabel = `${qrHref.replace(/^https?:\/\//, '')}` // sem protocolo, pro label

  // VARIANTE (copy): `reconnect` se o aparelho JÁ vinculou uma conta e a sessão
  // caiu (marcador durável); `link` se é base instalada logada só no Xtream sem
  // nunca ter vinculado; `entry` caso contrário. `isAuthed` é reativo (Xtream);
  // hasSwipeSession()/wasSwipeLinked() são por-disco — no boot já resolvidos.
  // A decisão é pura (gate-variant.ts) e testável; o marcador é UX-only, nunca
  // libera o app (quem faz isso é a sessão, no RootRoute).
  const isAuthed = useSessionStore((s) => s.isAuthed)
  const variant = resolveGateVariant({
    isAuthed,
    hasSwipeSession: hasSwipeSession(),
    wasLinked: wasSwipeLinked(),
  })
  const ns = `gate.${variant}`
  // A porta "Sair da conta" existe pra quem está preso num estado autenticado
  // inconsistente: base instalada (link) OU sessão caída de quem já vinculou
  // (reconnect). Na entrada fria não há conta pra sair.
  const showSignOut = variant === 'link' || variant === 'reconnect'

  // BACK e Sair = confirmar SAIR do app (gate é raiz, sem dead-end).
  // Com qualquer dialog aberto, BACK fecha o dialog (nunca sai por baixo dele).
  const askExit = () => setConfirmExit(true)
  useBackKey(() => {
    if (exitFailed) {
      dismissExitFailed()
      return
    }
    if (confirmSignOut) {
      setConfirmSignOut(false)
      return
    }
    if (confirmExit) {
      setConfirmExit(false)
      return
    }
    askExit()
  })

  // Sair da CONTA (só na variante `link`): o usuário está logado no Xtream e o
  // gate exige a conta Swipe. Sem esta porta ele não tem como trocar de conta
  // nem voltar atrás — só desinstalando (foi o que aconteceu).
  //
  // `signOut()` (sign-out.ts) é a fechadura ÚNICA: derruba sync, token (local +
  // revogação no servidor), credencial Xtream lembrada e o dado pessoal do
  // aparelho. Antes daqui saía só um logout de fachada, que navegava sem
  // encerrar a sessão — numa TV compartilhada isso entregava a credencial (e a
  // assinatura) do usuário anterior pro próximo. É AWAIT: só navegamos com o
  // estado já limpo, senão o RootRoute re-decide a rota lendo sessão meio-viva.
  const doSignOut = async () => {
    setConfirmSignOut(false)
    cancel() // aborta o polling pendente e solta a guarda do RootRoute
    await signOut()
    navigate('/', { replace: true })
  }

  // Caminho UNIVERSAL sem câmera (spec §3): quem não tem câmera acessa o site em
  // QUALQUER navegador e DIGITA o código em destaque no gate (bloco manualUrl +
  // codeGate dentro do PairingShell). Login e-mail/Google nativo na TV é refino
  // futuro (I8).

  return (
    <>
      <PairingShell
        stage={stage}
        onRetry={retry}
        onCancel={askExit}
        onConfigureHere={configureHere}
        focusPrefix="account-gate"
        // COPY HONESTA: aqui o botão SAI do app (não volta). Rótulo "Sair".
        cancelLabel={t('common.exit')}
        // Preso num estado autenticado inconsistente (link/reconnect): porta pra
        // trocar de conta.
        {...(showSignOut
          ? { extraAction: { label: t('gate.signOut'), onPress: () => setConfirmSignOut(true) } }
          : {})}
        waitingTitle={t(`${ns}.title`)}
        waitingSubtitle={t(`${ns}.subtitle`)}
        needsPreferenceMessage={t('gate.needsPreference')}
        qrSize={qrSizeForTier()}
        instructions={[t('gate.step1'), t('gate.step2'), t('gate.step3')]}
        manualUrlLabel={t('gate.manualUrl', { url: qrBaseLabel })}
        manualUrlHref={qrHref}
      />

      {confirmExit && (
        <ConfirmDialog
          title={t('common.exitTitle')}
          message={t('common.exitBody')}
          variant="danger"
          confirmLabel={t('common.exit')}
          // Confirmar aqui NÃO navega e NÃO desmonta esta tela (o comentário
          // abaixo explica por que o `cancel()` é evitado de propósito): pede a
          // saída e volta pra cá. Sem declarar, o foco fica órfão entre o OK e o
          // desfecho — e no webOS, onde a saída FUNCIONA mandando o app pro
          // background, `useAppExit` cancela o veredito, o aviso `hideCancel`
          // (única coisa que devolveria o foco) nunca sobe, e o gate volta do
          // launcher com o D-pad morto. Aqui é pior que nas Configurações: sem
          // D-pad o usuário não consegue parear a TV. Ver `shouldRestoreFocus`.
          //
          // MEDIDO nesta tela, não deduzido (é o que a distingue da Welcome, que
          // se cura por acidente num effect local): sem a declaração,
          // `temFocado=false` no instante do OK, 400 ms de janela órfã (40/63
          // amostras) e as 4 setas em n=0 depois do background/foreground.
          // Sonda: scripts/compat/topnav-dwell-probe.mjs, cenário 7.
          originStaysMountedOnConfirm
          onConfirm={() => {
            setConfirmExit(false)
            // NÃO chamamos cancel() aqui: ele navega pra `/` e, se a plataforma
            // não fechar o app, o RootRoute remontaria o gate reiniciando o
            // pairing (loop). O polling morre com o unmount do hook; se o app
            // seguir vivo, `exitFailed` assume e mostra a saída real.
            requestExit()
          }}
          onCancel={() => setConfirmExit(false)}
        />
      )}

      {confirmSignOut && (
        <ConfirmDialog
          title={t('gate.signOutTitle')}
          message={t('gate.signOutBody')}
          variant="danger"
          confirmLabel={t('gate.signOut')}
          onConfirm={() => void doSignOut()}
          onCancel={() => setConfirmSignOut(false)}
        />
      )}

      {/* A plataforma não fechou o app (Tizen sem API nativa, WebView, browser):
          nada de tela morta. Diz a verdade e devolve o controle ao usuário. */}
      {exitFailed && (
        <ConfirmDialog
          title={t('gate.exitFailedTitle')}
          message={t('gate.exitFailedBody')}
          confirmLabel={t('common.gotIt')}
          hideCancel
          onConfirm={dismissExitFailed}
          onCancel={dismissExitFailed}
        />
      )}
    </>
  )
}
